import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Globe, ArrowUpRight } from "lucide-react";
import { CONTACT, AREAS_SERVED } from "../../constants/data";
import { SectionHeading, Reveal } from "../Reveal";

export const Contact = () => (
  <section id="contact" data-testid="contact-section" className="py-24 lg:py-32 bg-brand-mist/60">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <div className="max-w-2xl">
        <SectionHeading title="Let's talk about the care you need" />
        <Reveal delay={0.15}>
          <p className="mt-6 text-lg leading-relaxed text-slate-600">
            Reach out for a free, no-obligation care consultation. Our friendly team is here to listen, answer your questions, and help you find the right support for yourself or your loved one — at your pace, and always without pressure.
          </p>
        </Reveal>
      </div>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-5">
          <Reveal>
            <div className="rounded-2xl border border-slate-200 bg-white p-8">
              <h3 className="font-display text-2xl text-brand-navy">{CONTACT.name}</h3>
              <ul className="mt-6 space-y-5">
                <li className="flex items-start gap-4">
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-teal/10 text-brand-teal">
                    <Phone className="h-5 w-5" strokeWidth={1.6} />
                  </span>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Phone</p>
                    <a href={CONTACT.phoneHref} data-testid="contact-phone-link" className="mt-1 block text-base font-semibold text-brand-navy hover:text-brand-teal transition-colors duration-300">
                      {CONTACT.phone}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-teal/10 text-brand-teal">
                    <Mail className="h-5 w-5" strokeWidth={1.6} />
                  </span>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Email</p>
                    <a href={CONTACT.emailHref} data-testid="contact-email-link" className="mt-1 block text-base font-semibold text-brand-navy hover:text-brand-teal transition-colors duration-300 break-all">
                      {CONTACT.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-teal/10 text-brand-teal">
                    <MapPin className="h-5 w-5" strokeWidth={1.6} />
                  </span>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Address</p>
                    <p className="mt-1 text-base font-semibold text-brand-navy">204/8 – 13049 76 Avenue<br />Surrey, BC V3W 2V7</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-teal/10 text-brand-teal">
                    <Clock className="h-5 w-5" strokeWidth={1.6} />
                  </span>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Business Hours</p>
                    <p className="mt-1 text-base font-semibold text-brand-navy">{CONTACT.hours}</p>
                    <p className="text-sm text-brand-teal font-semibold">{CONTACT.hoursNote}</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-teal/10 text-brand-teal">
                    <Globe className="h-5 w-5" strokeWidth={1.6} />
                  </span>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Website</p>
                    <p className="mt-1 text-base font-semibold text-brand-navy">{CONTACT.website}</p>
                  </div>
                </li>
              </ul>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={CONTACT.phoneHref}
                  data-testid="contact-call-button"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-teal px-6 py-3.5 text-base font-bold text-white hover:bg-brand-navy transition-colors duration-300 active:scale-95"
                >
                  <Phone className="h-4 w-4" strokeWidth={1.8} /> Call Now
                </a>
                <a
                  href={CONTACT.emailHref}
                  data-testid="contact-email-button"
                  className="inline-flex items-center gap-2 rounded-full border border-brand-navy/20 px-6 py-3.5 text-base font-bold text-brand-navy hover:bg-brand-navy hover:text-white transition-colors duration-300 active:scale-95"
                >
                  <Mail className="h-4 w-4" strokeWidth={1.8} /> Email Us
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-slate-200 bg-white p-7">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-teal">Areas We Serve</p>
              <p data-testid="areas-served-list" className="mt-3 text-base leading-loose text-slate-600 font-medium">
                {AREAS_SERVED.join("  •  ")}
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="flex flex-col">
          <div className="map-frame relative flex-1 min-h-[380px] rounded-2xl overflow-hidden border border-slate-200 shadow-[0_16px_40px_rgb(11,27,61,0.10)]">
            <iframe
              title="BMS Health Care Services location — 13049 76 Avenue, Surrey, BC"
              src="https://www.google.com/maps?q=13049+76+Avenue,+Surrey,+BC+V3W+2V7&output=embed"
              className="absolute inset-0 h-full w-full"
              style={{ border: 0 }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <Link
            to="/referral"
            data-testid="contact-referral-cta"
            className="mt-5 inline-flex items-center justify-between gap-3 rounded-2xl bg-brand-navy px-7 py-5 text-white hover:bg-brand-teal transition-colors duration-300 group"
          >
            <span>
              <span className="block font-display text-xl">Ready to refer a patient?</span>
              <span className="block text-sm text-slate-300 mt-0.5">Complete our secure Patient Referral Form online</span>
            </span>
            <ArrowUpRight className="h-6 w-6 shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" strokeWidth={1.6} />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);
